export const PRIVATI_PAGES = [
  {
    path: '/soluzioni/privati',
    kind: 'area',
    area: 'Privati',
    areaPath: '/soluzioni/privati',
    eyebrow: 'Area Privati',
    title: 'Assicurazioni per privati in Svizzera: casa, famiglia, mobilità e patrimonio.',
    summary: 'RC privata, economia domestica, auto, abitazione e protezione del reddito: coordiniamo le coperture attorno ai cambiamenti reali della tua vita.',
    seoTitle: 'Assicurazioni per privati in Svizzera | Broker',
    seoDescription: 'Check-up assicurativo per privati in Svizzera: RC privata, economia domestica, auto, casa, viaggi e protezione del reddito. Scopri doppioni e lacune.',
    keywords: ['assicurazioni privati svizzera', 'rc privata svizzera', 'economia domestica', 'assicurazione auto svizzera', 'broker assicurativo privati'],
    image: '/assets/soluzione-privati.png',
    updated: 'Giugno 2026',
    readingTime: '5 min',
    intro: [
      'Le esigenze assicurative di una persona cambiano con la vita. Quello che serve a uno studente che lascia casa non è quello che serve a una famiglia che compra un immobile: per questo organizziamo le soluzioni per situazioni, non per prodotti.',
      'Il check-up parte dalle polizze già attive e dagli eventi recenti — trasloco, convivenza, figli, auto, acquisto casa o nuovo lavoro — per capire dove aggiornare somme, persone assicurate e priorità.',
    ],
    sections: [
      {
        id: 'protezione-quotidiana', type: 'cards', title: 'La protezione quotidiana',
        cards: [
          { title: 'Responsabilità civile privata', text: 'Copre i danni che causi involontariamente ad altri. In Svizzera è spesso richiesta dai locatori.' },
          { title: 'Mobilia domestica', text: 'Protegge i tuoi beni da furto, incendio, acqua ed eventi naturali, a casa e spesso anche fuori.' },
          { title: 'Protezione giuridica', text: 'Supporto e copertura dei costi in caso di controversie: lavoro, locazione, circolazione, contratti.' },
          { title: 'Veicoli', text: 'Responsabilità civile, casco parziale o totale, assistenza: la copertura giusta dipende da valore e uso del veicolo.' },
          { title: 'Viaggi', text: 'Annullamento, assistenza e spese mediche all\u2019estero, da coordinare con la cassa malati.' },
          { title: 'Infortuni e reddito', text: 'Cosa succede se non puoi lavorare: indennità giornaliera e protezione del reddito.' },
        ],
      },
      {
        id: 'momenti-di-vita', type: 'paths', title: 'Le guide per i momenti importanti',
        links: [
          { href: '/guide/privati/andare-a-vivere-da-soli', label: 'Andare a vivere da soli', text: 'Il primo appartamento: cosa serve davvero e cosa può aspettare.' },
          { href: '/guide/privati/trasferirsi-in-svizzera', label: 'Trasferirsi in Svizzera', text: 'Assicurazioni, abitazione e adempimenti dei primi mesi.' },
          { href: '/guide/privati/importare-auto-in-svizzera', label: 'Importare un\u2019auto', text: 'Dogane, immatricolazione e copertura: la checklist completa.' },
          { href: '/guide/privati/comprare-casa-a-30-anni', label: 'Comprare casa a 30 anni', text: 'Finanziamento, coperture dell\u2019immobile e protezione del reddito.' },
          { href: '/guide/privati/mettersi-in-proprio', label: 'Mettersi in proprio', text: 'Da dipendente a indipendente: cosa non è più automatico.' },
        ],
      },
      {
        id: 'quando-verificare', type: 'checklist', title: 'Quando conviene una verifica delle coperture',
        lead: 'Una revisione periodica è utile soprattutto dopo i grandi cambiamenti.',
        items: [
          'Trasloco o cambio di abitazione',
          'Matrimonio, convivenza o separazione',
          'Nascita di un figlio',
          'Cambio di lavoro o passaggio all\u2019indipendenza',
          'Acquisto di un immobile o di un veicolo',
          'Avvio di un\u2019attività indipendente',
        ],
      },
    ],
    faq: [
      { q: 'La responsabilità civile privata è obbligatoria?', a: 'Non per legge in generale, ma è richiesta da quasi tutti i locatori ed è fortemente consigliata: un danno a terzi può costare molto più di anni di premi.' },
      { q: 'Ogni quanto conviene rivedere le polizze?', a: 'Indicativamente ogni 2-3 anni, e comunque a ogni cambiamento importante: trasloco, figli, nuovo lavoro, acquisti rilevanti.' },
      { q: 'Posso riunire tutte le polizze presso un unico referente?', a: 'Sì, è uno dei vantaggi di lavorare con un broker: un unico interlocutore che conosce la tua situazione completa e coordina le coperture.' },
    ],
    cta: { title: 'Richiedi un check-up assicurativo privato', text: 'Raccogliamo le tue polizze, individuiamo doppioni e lacune e ti proponiamo le priorità.', label: 'Richiedi un check-up' },
    related: ['/guide/privati/trasferirsi-in-svizzera', '/guide/privati/comprare-casa-a-30-anni'],
  },
  {
    path: '/guide/privati/andare-a-vivere-da-soli',
    kind: 'guide',
    area: 'Privati',
    areaPath: '/soluzioni/privati',
    eyebrow: 'Guida pratica · Privati',
    title: 'Andare a vivere da soli: le assicurazioni per la prima casa.',
    summary: 'Per studenti e giovani adulti: cosa serve davvero quando lasci la casa dei genitori, e cosa può aspettare.',
    updated: 'Giugno 2026',
    readingTime: '6 min',
    intro: [
      'Il primo appartamento è un passo importante, anche dal punto di vista assicurativo: alcune coperture che prima erano comprese nella polizza dei genitori ora devi attivarle tu.',
    ],
    sections: [
      {
        id: 'cosa-cambia', type: 'text', title: 'Cosa cambia quando lasci casa',
        paragraphs: ['Con il trasloco cambia la tua responsabilità verso terzi, verso il locatore e verso i tuoi beni.'],
        bullets: [
          'Diventi responsabile dei danni che causi ad altri',
          'L\u2019appartamento in affitto va restituito senza danni',
          'I tuoi beni non sono più coperti dalla polizza dei genitori',
          'La cassa malati va aggiornata con il nuovo domicilio',
        ],
      },
      {
        id: 'coperture', type: 'cards', title: 'Le coperture da valutare',
        cards: [
          { title: 'Responsabilità civile privata', text: 'La priorità numero uno: richiesta da quasi tutti i locatori, copre i danni a terzi e spesso quelli locatizi.' },
          { title: 'Mobilia domestica', text: 'Protegge i tuoi beni. Con pochi mobili il valore assicurato è basso e il premio contenuto.' },
          { title: 'Protezione giuridica', text: 'Utile per controversie con il locatore o sul lavoro. Valutabile anche in un secondo momento.' },
          { title: 'Cauzione d\u2019affitto', text: 'In alternativa al deposito bancario, esistono soluzioni di garanzia: confronta costi e condizioni.' },
        ],
      },
      {
        id: 'contratto-locazione', type: 'checklist', title: 'Prima di firmare il contratto di locazione',
        items: [
          'Leggere le responsabilità dell\u2019inquilino nel contratto',
          'Verificare l\u2019importo della cauzione richiesta',
          'Documentare lo stato dell\u2019immobile con fotografie datate',
          'Compilare con cura il verbale di consegna',
          'Chiarire chi risponde per eventuali danni preesistenti',
        ],
      },
      {
        id: 'budget', type: 'text', title: 'Il budget mensile realistico',
        paragraphs: [
          'Oltre all\u2019affitto, metti in conto: cassa malati, utenze, assicurazioni, trasporti e una piccola riserva per gli imprevisti. Le assicurazioni di base per un primo appartamento (responsabilità civile + mobilia) hanno in genere un costo annuo contenuto: vale la pena non rinunciarvi.',
        ],
      },
    ],
    faq: [
      { q: 'Serve davvero la mobilia domestica se ho pochi mobili?', a: 'Spesso sì: la polizza copre anche furto fuori casa e danni da acqua o incendio. Con valori bassi il premio è contenuto. In alcuni casi si può valutare di restare temporaneamente nella polizza familiare: va verificato con la compagnia.' },
      { q: 'Chi paga se rompo qualcosa nell\u2019appartamento in affitto?', a: 'I danni locatizi sono di regola coperti dalla responsabilità civile privata, se inclusa. Verifica questa voce nelle condizioni prima di firmare la polizza.' },
      { q: 'Devo avvisare la cassa malati del trasloco?', a: 'Sì: il premio dipende anche dal luogo di domicilio. Comunica il cambio di indirizzo alla cassa e aggiorna i tuoi dati.' },
    ],
    cta: { title: 'Prepara il tuo primo pacchetto assicurativo', text: 'Due coperture essenziali, scelte bene: ti aiutiamo a partire con il piede giusto, senza spendere troppo.', label: 'Prepara il tuo pacchetto' },
    related: ['/guide/privati/trasferirsi-in-svizzera', '/guide/privati/mettersi-in-proprio'],
  },
  {
    path: '/guide/privati/trasferirsi-in-svizzera',
    kind: 'guide',
    area: 'Privati',
    areaPath: '/soluzioni/privati',
    eyebrow: 'Guida pratica · Privati',
    title: 'Trasferirsi in Svizzera: cosa considerare.',
    summary: 'Assicurazioni, abitazione, lavoro e adempimenti: la guida di ingresso per chi arriva in Svizzera.',
    updated: 'Giugno 2026',
    readingTime: '8 min',
    intro: [
      'Chi si trasferisce in Svizzera incontra un sistema assicurativo diverso da quello italiano: la cassa malati è obbligatoria, la responsabilità civile è richiesta dai locatori, la previdenza funziona a pilastri. Questa guida ti aiuta a mettere le cose in ordine.',
    ],
    sections: [
      {
        id: 'prime-priorita', type: 'checklist', title: 'Le prime priorità',
        items: [
          'Registrazione del domicilio e permesso di soggiorno',
          'Assicurazione malattia (cassa malati): obbligatoria entro i termini previsti',
          'Contratto di affitto e cauzione',
          'Responsabilità civile privata e mobilia domestica',
          'Copertura infortuni: tramite il datore di lavoro o la cassa malati',
          'Veicolo, patente ed eventuale importazione',
        ],
      },
      {
        id: 'percorsi', type: 'cards', title: 'Dipendente, frontaliere o indipendente',
        lead: 'Le esigenze cambiano molto in base al tuo status lavorativo.',
        cards: [
          { title: 'Dipendente', text: 'Infortuni e previdenza professionale passano dal datore di lavoro. Restano da organizzare cassa malati, RC e mobilia.' },
          { title: 'Frontaliere', text: 'Regole particolari per assistenza sanitaria, fiscalità e previdenza: una verifica dedicata evita doppie coperture o lacune.' },
          { title: 'Indipendente', text: 'Infortuni, indennità giornaliera e previdenza sono a tuo carico: leggi anche la guida su mettersi in proprio.' },
        ],
      },
      {
        id: 'primi-giorni', type: 'timeline', title: 'I primi giorni: una sequenza possibile',
        steps: [
          { label: 'Settimana 1', text: 'Registrazione al controllo abitanti, permesso, apertura conto bancario.' },
          { label: 'Settimane 2-4', text: 'Scelta della cassa malati e invio della documentazione. Attivazione di RC e mobilia domestica.' },
          { label: 'Primi mesi', text: 'Verifica della copertura infortuni, veicolo e patente, previdenza e coordinamento con le coperture estere.' },
        ],
      },
      {
        id: 'errori', type: 'errors', title: 'Errori frequenti di chi arriva',
        items: [
          'Rimandare la scelta della cassa malati oltre i termini',
          'Mantenere coperture estere senza verificarne la validità in Svizzera',
          'Non coordinare infortuni e malattia, con doppie coperture o lacune',
          'Firmare il contratto di affitto senza la responsabilità civile richiesta',
          'Sottovalutare previdenza e fiscalità del nuovo paese',
        ],
      },
      {
        id: 'attenzione-cassa', type: 'warning', title: 'La cassa malati non aspetta',
        text: 'L\u2019assicurazione malattia è obbligatoria per chi si stabilisce in Svizzera e va attivata entro i termini previsti dall\u2019arrivo, con decorrenza retroattiva. Prima ti muovi, più semplice è la gestione amministrativa.',
      },
    ],
    faq: [
      { q: 'Entro quanto devo scegliere la cassa malati?', a: 'Di regola entro tre mesi dall\u2019arrivo, con copertura retroattiva dalla data di entrata. I premi vanno pagati dalla decorrenza, quindi prima scegli, meglio è.' },
      { q: 'Le mie assicurazioni italiane valgono ancora?', a: 'Dipende dalla copertura: molte polizze hanno validità territoriale limitata. Falle verificare prima di contare su di esse in Svizzera.' },
      { q: 'Serve la responsabilità civile per affittare?', a: 'Non è obbligatoria per legge, ma quasi tutti i locatori la richiedono come condizione del contratto.' },
      { q: 'Cosa cambia se sono frontaliere?', a: 'Assistenza sanitaria, fiscalità e previdenza seguono regole specifiche e accordi bilaterali. Merita una consulenza dedicata prima di decidere.' },
    ],
    cta: { title: 'Organizza il tuo trasferimento assicurativo', text: 'Un colloquio per mettere in ordine cassa malati, abitazione e coperture di base: senza errori e senza doppioni.', label: 'Organizza il trasferimento' },
    related: ['/guide/privati/andare-a-vivere-da-soli', '/guide/privati/importare-auto-in-svizzera'],
  },
  {
    path: '/guide/privati/importare-auto-in-svizzera',
    kind: 'guide',
    area: 'Privati',
    areaPath: '/soluzioni/privati',
    eyebrow: 'Guida pratica · Privati',
    title: 'Importare un\u2019auto in Svizzera: dogane, immatricolazione e assicurazione.',
    summary: 'La checklist ordinata per chi porta un veicolo in Svizzera, dai documenti alle targhe.',
    updated: 'Giugno 2026',
    readingTime: '7 min',
    intro: [
      'Importare un veicolo in Svizzera richiede alcuni passaggi amministrativi precisi. L\u2019assicurazione entra in gioco due volte: durante il trasferimento e al momento dell\u2019immatricolazione.',
    ],
    sections: [
      {
        id: 'prima', type: 'checklist', title: 'Prima dell\u2019importazione',
        items: [
          'Documenti di proprietà e carta di circolazione del veicolo',
          'Verifica della conformità tecnica per la Svizzera',
          'Stima di imposte e dazi all\u2019importazione',
          'Organizzazione del trasporto e della copertura durante il trasferimento',
          'Eventuale targa temporanea per il viaggio',
        ],
      },
      {
        id: 'dopo', type: 'timeline', title: 'Dopo l\u2019arrivo: la sequenza',
        steps: [
          { label: 'Passo 1', title: 'Dichiarazione doganale', text: 'Il veicolo va dichiarato alla dogana con i documenti di proprietà e le eventuali imposte.' },
          { label: 'Passo 2', title: 'Controllo tecnico', text: 'Perizia presso la sezione della circolazione per verificare la conformità del veicolo.' },
          { label: 'Passo 3', title: 'Attestato assicurativo', text: 'La compagnia trasmette l\u2019attestato assicurativo elettronico: senza, niente targhe.' },
          { label: 'Passo 4', title: 'Immatricolazione', text: 'Con controllo tecnico e attestato puoi richiedere targhe e licenza di circolazione svizzera.' },
        ],
      },
      {
        id: 'copertura', type: 'cards', title: 'Come scegliere la copertura',
        cards: [
          { title: 'Responsabilità civile', text: 'Obbligatoria: copre i danni a terzi. L\u2019attestato assicurativo è il presupposto per le targhe.' },
          { title: 'Casco parziale', text: 'Furto, incendio, eventi naturali, rottura vetri, collisioni con animali.' },
          { title: 'Casco totale', text: 'Aggiunge i danni da collisione propri. Sensata per veicoli di valore o recenti.' },
          { title: 'Estensioni utili', text: 'Assistenza, protezione colpa grave, valore venale maggiorato, veicolo sostitutivo.' },
        ],
      },
      {
        id: 'costi', type: 'text', title: 'Il costo complessivo da mettere in conto',
        bullets: [
          'Prezzo di acquisto e trasporto del veicolo',
          'Imposte e dazi all\u2019importazione',
          'Controllo tecnico ed eventuali adeguamenti',
          'Immatricolazione e targhe',
          'Premio assicurativo annuo',
          'Manutenzione iniziale',
        ],
      },
    ],
    faq: [
      { q: 'Posso guidare in Svizzera con targhe estere?', a: 'Per periodi limitati e in condizioni specifiche. Chi si stabilisce in Svizzera deve immatricolare il veicolo entro i termini previsti.' },
      { q: 'L\u2019attestato assicurativo cos\u2019è esattamente?', a: 'È la conferma elettronica della copertura RC che la compagnia invia alla sezione della circolazione. Senza attestato non è possibile immatricolare il veicolo.' },
      { q: 'Conviene la casco totale su un\u2019auto importata?', a: 'Dipende dal valore del veicolo e dal tuo profilo di rischio. Su veicoli recenti o di valore è spesso consigliabile; su veicoli datati si valuta la casco parziale.' },
    ],
    cta: { title: 'Richiedi una verifica prima dell\u2019immatricolazione', text: 'Controlliamo insieme documenti, tempistiche e la copertura giusta per il tuo veicolo.', label: 'Richiedi una verifica' },
    related: ['/guide/privati/trasferirsi-in-svizzera', '/guide/privati/andare-a-vivere-da-soli'],
  },
  {
    path: '/guide/privati/comprare-casa-a-30-anni',
    kind: 'guide',
    area: 'Privati',
    areaPath: '/soluzioni/privati',
    eyebrow: 'Guida pratica · Privati',
    title: 'Comprare casa a 30 anni: finanziamento, coperture e protezione del reddito.',
    summary: 'La prima abitazione di proprietà è anche una decisione assicurativa: ecco cosa valutare prima di firmare.',
    updated: 'Giugno 2026',
    readingTime: '8 min',
    intro: [
      'Comprare la prima casa significa assumersi un impegno finanziario di lungo periodo. Le assicurazioni giuste non proteggono solo i muri: proteggono la tua capacità di pagare il mutuo anche se qualcosa va storto.',
    ],
    sections: [
      {
        id: 'prima-acquisto', type: 'checklist', title: 'Prima dell\u2019acquisto',
        items: [
          'Capitale proprio disponibile e sua composizione',
          'Sostenibilità del finanziamento rispetto al reddito',
          'Costi accessori: notaio, registro, eventuali ristrutturazioni',
          'Manutenzione ordinaria e straordinaria nel tempo',
          'Imposte e spese ricorrenti dell\u2019immobile',
          'Riserva di liquidità per gli imprevisti',
        ],
      },
      {
        id: 'coperture', type: 'cards', title: 'Le coperture da valutare',
        cards: [
          { title: 'Assicurazione stabile', text: 'Incendio, eventi naturali, acqua: protegge il fabbricato. In molti cantoni esistono regole specifiche.' },
          { title: 'RC del proprietario', text: 'Copre i danni che l\u2019immobile può causare a terzi, ad esempio per difetti di manutenzione.' },
          { title: 'Protezione giuridica immobiliare', text: 'Controversie con imprese, vicini o amministrazione: costi legali coperti.' },
          { title: 'Rischio decesso e incapacità', text: 'Protegge il mutuo se viene meno il reddito: fondamentale per chi compra con un finanziamento.' },
        ],
      },
      {
        id: 'proteggere-finanziamento', type: 'text', title: 'Proteggere il finanziamento',
        paragraphs: [
          'Il muto è un impegno che dura decenni. Cosa succede in caso di decesso, incapacità lavorativa o separazione? Senza una protezione adeguata, il rischio ricade sulla famiglia o sul partner.',
          'Le soluzioni tipiche combinano assicurazione rischio decesso, copertura per incapacità di guadagno e coordinamento con la previdenza. Anche il terzo pilastro può essere coinvolto nell\u2019acquisto: va gestito con attenzione.',
        ],
      },
      {
        id: 'caso', type: 'case', title: 'Un caso tipico',
        caseStudy: {
          situation: 'Coppia di 31 anni, due redditi da dipendenti, primo appartamento di proprietà con ipoteca all\u201980%.',
          problem: 'La banca approva il finanziamento, ma nessuno ha verificato cosa succede al mutuo se uno dei due non può più lavorare.',
          risks: ['Riduzione del reddito in caso di malattia prolungata', 'Mutuo insostenibile con un solo stipendio', 'Coperture dell\u2019immobile non coordinate con il contratto di compravendita'],
          decisions: ['Analisi della sostenibilità con un solo reddito', 'Copertura incapacità di guadagno per entrambi', 'Assicurazione rischio decesso collegata al debito ipotecario', 'Coordinamento tra stabile, RC proprietario e protezione giuridica'],
          result: 'Finanziamento mantenuto, con un piano di protezione proporzionato al debito e ai redditi reali.',
        },
      },
    ],
    faq: [
      { q: 'Posso usare il secondo o terzo pilastro per comprare casa?', a: 'In determinate condizioni sì, con effetti su previdenza e fiscalità da valutare con attenzione. È una decisione da coordinare, non da improvvisare.' },
      { q: 'L\u2019assicurazione stabile è obbligatoria?', a: 'Dipende dal cantone: in alcuni esiste l\u2019assicurazione stabili cantonale obbligatoria, in altri la copertura è privata. La banca richiede comunque che l\u2019immobile sia assicurato.' },
      { q: 'Serve davvero la copertura rischio decesso?', a: 'Per chi compra con un mutuo e un partner o una famiglia, è spesso la protezione più importante: garantisce che il debito non ricada su chi resta.' },
    ],
    cta: { title: 'Valuta la protezione della tua prima casa', text: 'Prima di firmare, verifichiamo insieme finanziamento, coperture dell\u2019immobile e protezione del reddito.', label: 'Valuta la protezione' },
    related: ['/soluzioni/previdenza', '/guide/privati/mettersi-in-proprio'],
  },
  {
    path: '/guide/privati/mettersi-in-proprio',
    kind: 'guide',
    area: 'Privati',
    areaPath: '/soluzioni/privati',
    eyebrow: 'Guida pratica · Privati',
    title: 'Mettersi in proprio: da dipendente a indipendente senza lacune.',
    summary: 'Quando lasci il posto fisso, alcune protezioni non sono più automatiche. Ecco cosa organizzare prima di partire.',
    updated: 'Giugno 2026',
    readingTime: '8 min',
    intro: [
      'Da dipendente, infortuni, indennità giornaliera e previdenza professionale passano dal datore di lavoro. Da indipendente, tutto questo diventa una tua responsabilità: il momento del passaggio è quello in cui si creano le lacune più pericolose.',
    ],
    sections: [
      {
        id: 'cosa-cambia', type: 'cards', title: 'Cosa cambia rispetto al posto fisso',
        cards: [
          { title: 'Infortuni', text: 'Non sei più coperto dalla LAINF del datore: la copertura va attivata da te, tramite cassa malati o assicurazione dedicata.' },
          { title: 'Indennità giornaliera', text: 'In caso di malattia nessuno ti versa lo stipendio: serve una copertura per la perdita di reddito.' },
          { title: 'Previdenza professionale', text: 'Il secondo pilastro non è più automatico: la previdenza va riorganizzata da zero.' },
          { title: 'Responsabilità professionale', text: 'Gli errori nel tuo lavoro diventano un rischio tuo: valuta una RC professionale.' },
        ],
      },
      {
        id: 'checklist', type: 'checklist', title: 'La checklist prima di iniziare',
        items: [
          'Definire l\u2019attività e la forma giuridica',
          'Stimare fatturato e costi fissi del primo anno',
          'Verificare la copertura sociale (AVS e previdenza)',
          'Proteggere il reddito in caso di malattia o infortunio',
          'Separare i rischi privati da quelli professionali',
          'Valutare RC professionale e protezione giuridica',
        ],
      },
      {
        id: 'caso', type: 'case', title: 'Un caso tipico',
        caseStudy: {
          situation: 'Consulente informatico, 34 anni, lascia il posto fisso per lavorare in proprio con i primi due clienti acquisiti.',
          problem: 'Dopo le dimissioni scopre che infortuni e indennità giornaliera non sono più coperti, e che la sua previdenza si è fermata.',
          risks: ['Perdita di reddito in caso di malattia oltre i primi giorni', 'Nessuna copertura infortuni professionale', 'Lacuna previdenziale che si accumula anno dopo anno', 'Responsabilità verso i clienti per errori di consulenza'],
          decisions: ['Indennità giornaliera con attesa calibrata sui risparmi', 'Copertura infortuni tramite assicurazione dedicata', 'Piano previdenziale con terzo pilastro e soluzione LPP per indipendenti', 'RC professionale per i rischi di consulenza'],
          result: 'Passaggio completato senza lacune, con costi assicurativi pianificati nel budget del primo anno.',
        },
      },
      {
        id: 'forma-giuridica', type: 'warning', title: 'Indipendente o società?',
        text: 'La scelta tra ditta individuale, Sagl o SA cambia responsabilità, previdenza e coperture necessarie. Approfondiamo il confronto nella guida sulle forme giuridiche.',
      },
    ],
    faq: [
      { q: 'Da quando devo attivare le coperture da indipendente?', a: 'Idealmente dal primo giorno di attività. Alcune coperture, come l\u2019indennità giornaliera, hanno periodi d\u2019attesa: organizzarle prima evita vuoti.' },
      { q: 'Quanto costa proteggersi da indipendente?', a: 'Dipende da reddito, attività e coperture scelte. In fase di avvio conviene stabilire le priorità: prima i rischi che compromettono reddito e continuità, poi le estensioni.' },
      { q: 'La previdenza è obbligatoria per gli indipendenti?', a: 'L\u2019AVS è obbligatoria; il secondo pilastro no, ma rinunciarvi crea lacune rilevanti. Esistono soluzioni specifiche per indipendenti da valutare.' },
    ],
    cta: { title: 'Analizza il passaggio all\u2019attività indipendente', text: 'Prima di dare le dimissioni, costruiamo insieme il piano di protezione: reddito, infortuni, previdenza e responsabilità.', label: 'Analizza il passaggio' },
    related: ['/guide/aziende/forme-giuridiche-svizzera', '/guide/aziende/aprire-un-azienda'],
  },
];
